import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// About standout — faithful rewrite of legacy molecules/AboutStandout (Strapi
// sections.about-new-standout, e.g. "Why Cymmetri / How we stand out"). Centered
// header over cards laid out as a top row of three then a row of two; each card
// has an icon in a white circle. Mobile scrolls the cards horizontally.
export const aboutStandoutSchema = z.object({
  eyebrow: z.string().optional(),
  heading: z.string().min(1),
  description: z.string().optional(),
  cards: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().optional(),
        imageUrl: z.string().optional(),
      }),
    )
    .min(1),
});

type AboutStandoutProps = z.infer<typeof aboutStandoutSchema>;

const Section = styled.section`
  background: #ffffff;
  max-width: 112rem;
  margin: 0 auto;
  padding: 3rem 1.6rem 2rem;

  @media (min-width: 48em) {
    padding: 16rem 1.6rem 12rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  font-size: 1.6rem;
  letter-spacing: 5px;
  text-transform: uppercase;
  text-align: center;

  @media (min-width: 48em) {
    font-size: 1.8rem;
  }
`;

const Heading = styled.h2`
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.8rem;
  letter-spacing: -2px;
  text-align: center;
  max-width: 30rem;

  @media (min-width: 48em) {
    font-size: 5.6rem;
    max-width: 54.8rem;
  }
`;

const Description = styled.p`
  margin: 0.8rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;
  text-align: center;
  max-width: 36rem;

  @media (min-width: 48em) {
    font-size: 1.8rem;
    max-width: 54.8rem;
  }
`;

const Rows = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 25rem;
  gap: 1.6rem;
  overflow-x: auto;
  margin-top: 4rem;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 48em) {
    grid-auto-flow: row;
    grid-auto-columns: unset;
    grid-template-columns: repeat(6, 1fr);
    gap: 2.4rem;
    overflow: visible;
    margin-top: 6rem;
  }
`;

const Card = styled.div<{ $span?: number }>`
  padding: 1.6rem;
  border-radius: 1rem;
  background: #f0f4ff;
  box-shadow: 1px -1px 0px 0px #b1c1e7 inset;

  @media (min-width: 48em) {
    padding: 2.4rem;
    border-radius: 1.6rem;
    grid-column: span ${({ $span }) => $span ?? 2};
  }
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 5rem;
  background: #ffffff;
  border-radius: 50%;

  @media (min-width: 48em) {
    width: 8rem;
    height: 8rem;
  }
`;

const Icon = styled.img`
  width: 3rem;
  height: 3rem;
  object-fit: contain;

  @media (min-width: 48em) {
    width: 5.2rem;
    height: 5.2rem;
  }
`;

const CardTitle = styled.p`
  margin: 1rem 0 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 1.8rem;
  letter-spacing: -0.01em;

  @media (min-width: 48em) {
    margin-top: 3rem;
    font-size: 2.4rem;
    line-height: 3.2rem;
  }
`;

const CardDesc = styled.p`
  margin: 0.5rem 0 0;
  color: #4e4f52;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 2.1rem;

  @media (min-width: 48em) {
    font-size: 1.8rem;
    line-height: 2.7rem;
  }
`;

function AboutStandout({ eyebrow, heading, description, cards }: AboutStandoutProps) {
  // Desktop layout mirrors legacy: first three cards span two of six columns
  // (a row of 3); the rest span three (a row of 2). A trailing single card
  // spans all six so it stays full-width rather than half.
  const rest = cards.length - 3;
  const restSpan = rest === 1 ? 6 : 3;
  return (
    <Section>
      <Header>
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Header>
      <Rows>
        {cards.map((card, i) => (
          <Card key={`${card.title}-${i}`} $span={i < 3 ? 2 : restSpan}>
            {card.imageUrl ? (
              <IconCircle>
                <Icon src={card.imageUrl} alt="" />
              </IconCircle>
            ) : null}
            <CardTitle>{card.title}</CardTitle>
            {card.description ? <CardDesc>{card.description}</CardDesc> : null}
          </Card>
        ))}
      </Rows>
    </Section>
  );
}

export const aboutStandoutBlock: BlockDefinition<AboutStandoutProps> = {
  type: 'aboutStandout',
  schema: aboutStandoutSchema,
  Renderer: AboutStandout,
};
